/* eslint-disable */
// @ts-nocheck
import { prop, indexBy, keys } from 'ramda';
import { createSelector } from 'reselect';

import {
  Email,
  getPrimaryInfo,
  ILegalEntityDTO,
  IPersonDto,
  MemberType,
  selectPartners,
} from '@qwealth/qdata';

import { selectPersonsRoot } from './personsSelectors';
import { MemberOption } from '../models/MemberOption';
import { HouseholdMetadataState } from '../models/HouseholdMetadataState';
import { orderMembersByType } from '../refData/memberOrder';

export const selectCurrentHouseholdProfiles = selectPersonsRoot;

export const selectHouseholdMetadata = (state): HouseholdMetadataState =>
  state.householdMetadata || {};

export const selectHouseholdMembers = state => selectHouseholdMetadata(state).members || [];

export const selectHouseholdLegalEntities = (state): Array<ILegalEntityDTO> =>
  selectHouseholdMetadata(state).legalEntities || [];

export const selectHouseholdLegalEntityMap = createSelector(
  selectHouseholdLegalEntities,
  legalEntities => {
    const map = {};
    legalEntities.forEach(e => (map[e.QID] = e), map);
    return map;
  },
);

export const selectMemberAndEntityQidToNameMap = createSelector(
  selectHouseholdMembers,
  selectHouseholdLegalEntities,
  (members, legalEntities): Record<string, string> => {
    let map = members.reduce(
      (prev, member) => ({
        ...prev,
        [member.QID]: `${member.firstName} ${member.lastName}`,
      }),
      {},
    );
    map = legalEntities.reduce(
      (prev, legalEntity) => ({
        ...prev,
        [legalEntity.QID]: legalEntity.name,
      }),
      map,
    );
    return map;
  },
);

export const selectHouseholdMembersInOrder = createSelector(selectHouseholdMembers, members =>
  members.slice().sort(orderMembersByType),
);

export const selectHouseholdMembersOptions = createSelector(
  selectHouseholdMembersInOrder,
  members =>
    members.map(({ firstName, QID, iaCode, insuranceCode }) => ({
      QID,
      name: firstName,
      iaCode,
      insuranceCode,
    })),
);

const clientMemberTypes: MemberType[] = ['Primary Client', 'Secondary Client', 'Additional Client'];
export const selectClientMembers: () => Array<IPersonDto> = createSelector(
  selectHouseholdMembersInOrder,
  members =>
    members.filter(member => clientMemberTypes.includes(member.householdMembers[0].memberType)),
);

export const selectOtherMembers: () => Array<IPersonDto> = createSelector(
  selectHouseholdMembers,
  members =>
    members.filter(member => !clientMemberTypes.includes(member.householdMembers[0].memberType)),
);

export const selectClientMembersOptions: () => Array<MemberOption> = createSelector(
  selectClientMembers,
  members =>
    members.map(({ firstName, QID, contactEmail }) => {
      let primaryEmail = '';
      const email = getPrimaryInfo(contactEmail) as Email;
      if (email) {
        primaryEmail = email.emailAddress.toLowerCase();
      }
      return {
        QID,
        name: firstName,
        primaryEmail,
      };
    }),
);

export const selectAllMembersOptions: () => Array<MemberOption> = createSelector(
  selectHouseholdMembersInOrder,
  members =>
    members.map(({ firstName, QID, contactEmail }) => {
      let primaryEmail = '';
      const email = getPrimaryInfo(contactEmail);
      if (email) {
        primaryEmail = email.emailAddress.toLowerCase();
      }
      return {
        QID,
        name: firstName,
        primaryEmail,
      };
    }),
);

export const selectHouseholdMembersById = createSelector<any, any, Record<string, any>>(
  selectHouseholdMembersInOrder,
  indexBy(prop('QID')),
);

export const selectHouseholdMemberIds = createSelector(selectHouseholdMembersById, keys);

export const selectHouseholdPartner = createSelector(
  selectPartners,
  selectHouseholdMetadata,
  (partners, metadata) => partners.find(p => p.id === metadata.partnerID),
);
